import {AppIconButton, AppSelect} from "../index";
import AppBox from "../Box";
import IconSortDown from "../icons/sort-down";
import IconSortUp from "../icons/sort-up";
import {SortOrderEnum} from "../../enums/sort-order-enum";
import React, {useEffect, useMemo, useRef} from "react";
import useFilter from "../../../hooks/useFilter";
import {ISelectOptions} from "./Select";
import IconDismiss from "../icons/dismiss";
import IconSearch from "../icons/search";
import debounce from "../../utilities/debounce";
import {UserStatusEnum} from "../../enums/user-status-enum";

interface FilterToolbarProps {
    sortByOptions?: ISelectOptions[]
    statusOptions?: ISelectOptions[]
    enableSorting?: boolean,
    enableSearch?: boolean,
    enableStatus?: boolean,
    reset?:boolean,
}

const defaultSortByOptions = [
    {
        title: "Name",
        value: "name",
    },
    {
        title: "Created Date",
        value: "createdAt",
    }
]

const defaultStatusOptions = [
    {
        title: "All",
        value: '',
    },
    {
        title: "Active",
        value: UserStatusEnum.Active.toString(),
    },
    {
        title: "Inactive",
        value: UserStatusEnum.Deactive?.toString(),
    }
]
const FilterToolbar: React.FC<FilterToolbarProps> = ({
                                                         sortByOptions = defaultSortByOptions,
                                                         statusOptions = defaultStatusOptions,
                                                         enableSorting = true,
                                                         enableSearch = true,
                                                         enableStatus = true,
                                                         reset = false
                                                     }) => {
    // Filter options
    const {
        sortBy,
        sortOrder,
        searchText,
        sortOrderChange,
        sortByChange,
        onSearch,
        status,
        statusChange
    } = useFilter();

    const [localText, setLocalText] = React.useState<string>(searchText || '');


    // Selected sort by option
    const selectedSortBy = useMemo(() => sortByOptions.find((option) => option.value === sortBy), [sortByOptions, sortBy]);

    // Selected status option
    const selectedStatus = useMemo(() => statusOptions.find((option) => option.value === status), [statusOptions, status]);

    // Ref to track latest onSearch function
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    });

    // Create stable debounce function
    const debounceSearchRef = useRef(
        debounce((value: string) => {
            onSearchRef.current(value);
        }, 500)
    );

    // Cancel pending debounces on unmount
    useEffect(() => {
        return () => {
            debounceSearchRef.current.cancel?.();
        };
    }, []);

    //Set text change and call debounce
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalText(value)
        debounceSearchRef.current(value);
    };

    // Clear search
    const handleClear = () => {
        setLocalText('')
        debounceSearchRef.current('');
    }

    useEffect(()=>{
        if(reset){
            handleClear()
        }
    },[reset])

    return (
        <section role="search" aria-label="Filter Toolbar" className="filter-toolbar flex justify-content-end">
            <AppBox gap={'2xs'} alignItems={'center'}>
                {enableSearch && <div className="ee-search-box">
                    <input
                        type="search"
                        value={localText}
                        onChange={handleTextChange}
                        className="ee-input ee-input-search"
                        placeholder={"Search"}
                        aria-label="Search input"
                    />
                    <div className="search-actions">
                        {localText ? (
                            <span onClick={handleClear} role="button" aria-label="Clear search">
                                <IconDismiss/>
                            </span>
                        ) : (
                            <IconSearch/>
                        )}
                    </div>
                </div>}
                {enableStatus && <div>
                    <AppSelect options={statusOptions}
                               aria-label="Status filter"
                               className="status-filter"
                               currentOption={selectedStatus}
                               onChange={(e: ISelectOptions) => statusChange(e.value as string)}/>
                </div>}
                {enableSorting && <AppBox className="sort-section">
                    <AppSelect options={sortByOptions}
                               aria-label="Sort By"
                               currentOption={selectedSortBy}
                               onChange={(e: ISelectOptions) => sortByChange(e.value as string)}/>
                    <AppIconButton variant={sortOrder === SortOrderEnum.Ascending ? 'filled' : "outline"}
                                   color={sortOrder === SortOrderEnum.Ascending ? 'primary' : 'gray'}
                                   onClick={() => sortOrderChange(SortOrderEnum.Ascending)}
                                   icon={IconSortUp()}
                                   aria-label="Sort Ascending"
                    />
                    <AppIconButton icon={IconSortDown()}
                                   variant={sortOrder === SortOrderEnum.Descending ? 'filled' : "outline"}
                                   color={sortOrder === SortOrderEnum.Descending ? 'primary' : "gray"}
                                   onClick={() => sortOrderChange(SortOrderEnum.Descending)}
                                   aria-label="Sort Descending"
                    />
                </AppBox>}
            </AppBox>
        </section>
    )
}

export default FilterToolbar